import { KlearDate } from '@klear/date'
import {
  dateFormatted,
  dateFormattedOrDash,
  domainListItemEffectiveDate,
  domainListItemNumber,
  listItemDateEpochDay
} from '@klear/order-event/list-display'
import { formatOrderDisplayName, orderListSummaryFromPurchaseOrders } from '@klear/order/list-display'
import { parseTimestamp } from '@klear/time/boundary'
import { useOrder } from '@klear/ui/hooks/use-order'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { OperationalPayments } from '@/app/orders/operational-payments'
import { Button } from '@klear/ui/components/button'
import {
  Plus,
  ChevronRight,
  CircleDollarSign,
  History,
  Truck,
  FileText,
  DollarSign,
  Star,
  Factory,
  FileCheck,
  Circle
} from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import toInteger from 'lodash/toInteger'
import toNumber from 'lodash/toNumber'
import { EventItem, EventFormWrapper } from '@klear/order-event-ui/event-item'
import { EventActions } from '@klear/order-event-ui/event-actions'
import { buildTimelineActionContext } from '@klear/order-event-ui/event-actions-availability'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import {
  buildTimelineSteps,
  flattenTimelineEvents,
  invoicedMoneyByCurrencyForPurchaseOrder,
  invoicedMoneyForPurchaseOrder,
  isPurchaseOrderEvent,
  orderEventTypeLabel,
  purchaseOrderDeliveryMarkers,
  showsStatusToTrader,
  timelineDocumentEventIds
} from '@klear/order-event-ui/event-timeline-utils'
import { InvoiceOfferView } from '@klear/invoice-offer-ui/invoice-offer-view'
import { orderEventFilePreviewContext } from '@klear/order-event/file-preview-display'
import { orderEventShowsStatus } from '@klear/order-event/presentation'
import { FileBadgeList } from '@klear/ui/components/file-badge-list'
import { OrderEventDocumentsProvider } from '@klear/order-event-ui/order-event-documents-context'
import { useOrderEventDocuments } from '@klear/ui/hooks/use-order-event-documents'
import { AdvanceRecoveryModal } from '@/app/receivables/advance-recovery-modal'
import { PurchaseOrder } from '@klear/purchase-order/purchase-order'
import {
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_INVOICE_OFFER
} from '@klear/constants'
import { trpc } from '@klear/ui/lib/trpc'
import { BasisPoints, MAX_BASIS_POINTS } from '@klear/basis-points'
import type { CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import type { OrderListSummary } from '@klear/order/order-list'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { PERMISSION_ACTION_ADD, ROUTE_ORDERS, ROUTE_PAYABLE } from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { Card, CardContent } from '@klear/ui/components/card'
import { Progress } from '@klear/ui/components/progress'
import { Badge } from '@klear/ui/components/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@klear/ui/components/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import EventStatus from '@klear/order-event-ui/event-status'
import { useOrderEventTimelineTree } from '@klear/ui/hooks/use-order-events'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId, timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'
import { operationalSupplierIdAsNumber } from '@klear/operational'
import toString from 'lodash/toString'

type PurchaseOrderNode = Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_PURCHASE_ORDER }>
type OperationalNode = Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_OPERATIONAL }>

function PurchaseOrderFormWrapper({
  traderId,
  orderId,
  onSuccess,
  onCancel
}: {
  traderId: number
  orderId: number
  onSuccess?: () => void
  onCancel?: () => void
}) {
  return (
    <EventFormWrapper
      eventType={PERMISSION_ENTITY_PURCHASE_ORDER}
      traderId={traderId}
      orderId={orderId}
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  )
}

function stepIcon(type: string): { icon: ReactNode; className: string } {
  switch (type) {
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return { icon: <FileText className="h-4 w-4" />, className: 'bg-muted text-muted-foreground' }
    case PERMISSION_ENTITY_OPERATIONAL:
      return {
        icon: <Factory className="h-4 w-4" />,
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
      }
    case PERMISSION_ENTITY_INVOICE:
      return {
        icon: <DollarSign className="h-4 w-4" />,
        className: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
      }
    case PERMISSION_ENTITY_INVOICE_OFFER:
      return {
        icon: <Star className="h-4 w-4" />,
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
      }
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return {
        icon: <FileCheck className="h-4 w-4" />,
        className: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300'
      }
    default:
      return { icon: <Circle className="h-4 w-4" />, className: 'bg-muted text-muted-foreground' }
  }
}

function otherCurrencyEntries(byCurrency: Map<CurrencyCode, Money>, poCurrency: CurrencyCode): [CurrencyCode, Money][] {
  return [...byCurrency.entries()]
    .filter(([currency]) => currency !== poCurrency)
    .sort(([a], [b]) => a.localeCompare(b))
}

function computePoSummary(po: PurchaseOrderNode, flatEvents: readonly OrderEventTimelineItem[]) {
  const poMoney = po.model.amount
  const invoicedByCurrency = invoicedMoneyByCurrencyForPurchaseOrder(flatEvents, timelineItemId(po))
  const invoicedMoney = invoicedMoneyForPurchaseOrder(flatEvents, timelineItemId(po), poMoney.currency)
  const otherCurrencyInvoiced = otherCurrencyEntries(invoicedByCurrency, poMoney.currency)
  const invoicedShare = poMoney.isZero() ? null : BasisPoints.fromMoney(invoicedMoney, poMoney)
  return {
    isPlaceholder: PurchaseOrder.isPlaceholder(po.model),
    poMoney,
    invoicedMoney,
    otherCurrencyInvoiced,
    invoicedShare
  }
}

function invoicedBadge({ isPlaceholder, invoicedMoney, poMoney, invoicedShare }: ReturnType<typeof computePoSummary>) {
  if (isPlaceholder) {
    return {
      label: 'Placeholder PO',
      className: 'bg-muted text-muted-foreground border-border'
    }
  }
  if (invoicedMoney.greaterThanOrEqualTo(poMoney) && poMoney.isPositive()) {
    return {
      label: 'Fully Invoiced',
      className:
        'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900'
    }
  }
  if (invoicedMoney.isPositive()) {
    return {
      label: `${invoicedShare?.toString({ digits: 1 }) ?? '-'} Invoiced`,
      className:
        'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-900'
    }
  }
  return {
    label: 'Not Invoiced',
    className: 'bg-muted text-muted-foreground border-transparent'
  }
}

function TimelineStepButton({
  active,
  last,
  icon,
  iconClassName,
  idDisplay,
  title,
  date,
  status,
  onClick
}: {
  active: boolean
  last: boolean
  icon: ReactNode
  iconClassName: string
  idDisplay?: string
  title: string
  date: string
  status?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'step' : undefined}
      className={`flex w-full gap-3 items-start rounded-xl px-3 pt-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'bg-muted' : 'hover:bg-muted/50'
      }`}
    >
      <div className="flex flex-col items-center self-stretch">
        <span
          className={`grid place-items-center h-8 w-8 rounded-full ${iconClassName} ${
            active ? 'ring-2 ring-offset-1 ring-border' : ''
          }`}
        >
          {icon}
        </span>
        <span className={`w-0.5 flex-1 min-h-3.5 ${last ? 'bg-transparent' : 'bg-border'}`} />
      </div>
      <div className={`flex-1 min-w-0 ${last ? 'pb-3' : 'pb-4'}`}>
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {idDisplay ? <span className="text-xs text-muted-foreground">{idDisplay}</span> : null}
          <span className={`text-sm ${active ? 'font-bold' : 'font-medium'}`}>{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{date}</span>
          {status ? (
            <Badge variant="outline" className="text-[10px]">
              {status}
            </Badge>
          ) : null}
        </div>
      </div>
      <ChevronRight className={`h-4 w-4 self-center ${active ? 'text-foreground' : 'text-muted-foreground/40'}`} />
    </button>
  )
}

export default function OrderDetail() {
  const { traderId, orderId } = useParams()
  const { user } = useAuth()
  const { urlPrefixApp } = useTrader(traderId)
  const { order, error, isLoading } = useOrder(toInteger(orderId), { traderId: toInteger(traderId) })
  useDocumentTitle(order?.idDisplay, 'Order', order?.customerOrganizationName)
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkEventId = searchParams.get('orderEventId')

  const [selectedPoId, setSelectedPoId] = useState<number | null>(null)
  const [focusEventId, setFocusEventId] = useState<number | null>(deepLinkEventId ? toNumber(deepLinkEventId) : null)
  const [showAddPO, setShowAddPO] = useState(false)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest')
  const [activityOpen, setActivityOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [advanceRecoveryTarget, setAdvanceRecoveryTarget] = useState<{ id: number; idDisplay: string } | null>(null)
  const [editingPoId, setEditingPoId] = useState<number | null>(null)
  const [addingChildType, setAddingChildType] = useState<
    typeof PERMISSION_ENTITY_INVOICE | typeof PERMISSION_ENTITY_OPERATIONAL | null
  >(null)

  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const traderIdNum = toNumber(traderId)

  const { events: timelineEvents, isLoading: isTimelineTreeLoading } = useOrderEventTimelineTree(toNumber(orderId))
  const timelineActionContext = buildTimelineActionContext(timelineEvents)
  const flatTimelineEvents = useMemo(() => flattenTimelineEvents(timelineEvents), [timelineEvents])
  const timelineEventIds = useMemo(() => timelineDocumentEventIds(timelineEvents), [timelineEvents])
  const { documentsFor } = useOrderEventDocuments(timelineEventIds, traderIdNum)
  const purchaseOrderNodes = useMemo(
    () =>
      (flatTimelineEvents.filter(isPurchaseOrderEvent) as PurchaseOrderNode[]).sort((a, b) =>
        KlearDate.compare(a.model.effectiveDate, b.model.effectiveDate)
      ),
    [flatTimelineEvents]
  )

  const totalInvoiced = useMemo(
    () =>
      Money.sum(
        purchaseOrderNodes.map((po) =>
          invoicedMoneyForPurchaseOrder(flatTimelineEvents, timelineItemId(po), po.model.amount.currency)
        )
      ),
    [purchaseOrderNodes, flatTimelineEvents]
  )

  const listSummary = useMemo<OrderListSummary | undefined>(
    () => orderListSummaryFromPurchaseOrders(purchaseOrderNodes.map((po) => po.model)),
    [purchaseOrderNodes]
  )

  const orderFilePreviewSummary = useMemo(
    () => ({
      idDisplay: order?.idDisplay ?? '',
      displayName: formatOrderDisplayName({
        customerOrganizationName: order?.customerOrganizationName,
        summary: listSummary,
        totalInvoicedAmount: totalInvoiced
      })
    }),
    [order, listSummary, totalInvoiced]
  )

  const selectedPo =
    purchaseOrderNodes.find((p) => timelineItemId(p) === selectedPoId) ??
    purchaseOrderNodes.find((p) => flattenTimelineEvents([p]).some((e) => timelineItemId(e) === focusEventId)) ??
    purchaseOrderNodes[0] ??
    null
  const selectedPoFilePreviewContext = useMemo(
    () =>
      selectedPo
        ? orderEventFilePreviewContext(selectedPo, flatTimelineEvents, traderIdNum, orderFilePreviewSummary)
        : null,
    [selectedPo, flatTimelineEvents, traderIdNum, orderFilePreviewSummary]
  )
  const selectedPoDocuments = selectedPo ? documentsFor(timelineItemId(selectedPo)) : []
  const selectedPoNodeId = selectedPo ? timelineItemId(selectedPo) : null
  const sortedChildren = useMemo(
    () =>
      [...(selectedPo?.children ?? [])].sort((a, b) => {
        const cmp =
          listItemDateEpochDay(domainListItemEffectiveDate(a)) - listItemDateEpochDay(domainListItemEffectiveDate(b))
        return sortOrder === 'oldest' ? cmp : -cmp
      }),
    [selectedPo, sortOrder]
  )

  const timelineSteps = useMemo(
    () =>
      buildTimelineSteps(
        (selectedPo ? flattenTimelineEvents([selectedPo]) : []).filter((e) => !isPurchaseOrderEvent(e)),
        (e) => listItemDateEpochDay(domainListItemEffectiveDate(e)),
        selectedPo ? purchaseOrderDeliveryMarkers(selectedPo.model) : [],
        sortOrder
      ),
    [selectedPo, sortOrder]
  )

  useEffect(() => {
    if (focusEventId == null || isTimelineTreeLoading || isLoading) return
    const timer = setTimeout(
      () => document.getElementById(`${focusEventId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      250
    )
    return () => clearTimeout(timer)
  }, [focusEventId, isTimelineTreeLoading, isLoading, selectedPoNodeId])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const getEventUrl = (n: { id: number }) => `${urlPrefixApp}/${ROUTE_ORDERS}/${orderId}?orderEventId=${n.id}`

  const addPaymentPath = (operational: OperationalNode) => {
    const params = new URLSearchParams({ operationalId: toString(timelineItemId(operational)) })
    const purchaseOrderId = timelineItemParentId(operational)
    if (purchaseOrderId != null) params.set('purchaseOrderId', toString(purchaseOrderId))
    const supplierId = operationalSupplierIdAsNumber(operational.model.supplierId)
    if (supplierId != null) params.set('supplierId', toString(supplierId))
    return `${urlPrefixApp}/${ROUTE_PAYABLE}/order/${orderId}?${params.toString()}`
  }

  function focusEvent(eventId: number | null) {
    setFocusEventId(eventId)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (eventId == null) next.delete('orderEventId')
        else next.set('orderEventId', `${eventId}`)
        return next
      },
      { replace: true }
    )
  }

  function selectPo(id: number) {
    setSelectedPoId(id)
    focusEvent(null)
    setShowAddPO(false)
  }

  function revealStep(eventId: number) {
    if (eventId === focusEventId) {
      document.getElementById(`${eventId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    focusEvent(eventId)
  }

  function selectEventById(eventId: number) {
    const owningPo = purchaseOrderNodes.find((p) =>
      flattenTimelineEvents([p]).some((e) => timelineItemId(e) === eventId)
    )
    if (owningPo) setSelectedPoId(timelineItemId(owningPo))
    focusEvent(eventId)
    setShowAddPO(false)
  }

  const renderEventItem = (event: OrderEventTimelineItem) => (
    <EventItem
      id={timelineItemId(event).toString()}
      event={event}
      bare
      leadingIcon={(e) => (
        <span className={`grid place-items-center h-7 w-7 rounded-full ${stepIcon(e.type).className}`}>
          {stepIcon(e.type).icon}
        </span>
      )}
      depth={0}
      traderId={toInteger(traderId)}
      orderId={toInteger(orderId)}
      getEventUrl={getEventUrl}
      isAdminContext={false}
      operationalPaymentsView={OperationalPayments}
      invoiceOfferView={InvoiceOfferView}
      onAdvanceRecovery={(offer) =>
        setAdvanceRecoveryTarget({
          id: timelineItemId(offer),
          idDisplay: offer.envelope.idDisplay
        })
      }
      onAddPayment={(operational) => navigate(addPaymentPath(operational))}
      timelineActionContext={timelineActionContext}
      timelineEventsLoading={isTimelineTreeLoading}
      filePreviewOrderSummary={orderFilePreviewSummary}
      focusedEventId={focusEventId}
    />
  )

  const canAddPO = user.hasPermission(PERMISSION_ENTITY_PURCHASE_ORDER, PERMISSION_ACTION_ADD)
  const canAddInvoice = user.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_ADD)
  const canAddOperational = user.hasPermission(PERMISSION_ENTITY_OPERATIONAL, PERMISSION_ACTION_ADD)
  const showAddPoForm = (showAddPO || purchaseOrderNodes.length === 0) && canAddPO && !isTimelineTreeLoading

  if (isLoading) {
    return (
      <div className="container mx-auto px-2 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading order details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !order) {
    navigate('/not-found', { replace: true })
    return null
  }

  const summary = selectedPo ? computePoSummary(selectedPo, flatTimelineEvents) : null

  return (
    <OrderEventDocumentsProvider orderEventIds={timelineEventIds} traderId={traderIdNum}>
      <div className="container mx-auto px-2 py-6">
        <div className="flex flex-col space-y-6">
          <div
            className={`sticky top-16 z-20 border-b bg-background py-3 group-has-[[data-collapsible=icon]]/sidebar-wrapper:top-12 ${
              scrolled ? 'border-border' : 'border-transparent'
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">{order.customerOrganizationName}</h1>
                <p className="text-muted-foreground">
                  {purchaseOrderNodes.length} purchase order{purchaseOrderNodes.length === 1 ? '' : 's'}
                  {order.idDisplay ? ` · ${order.idDisplay}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setActivityOpen(true)}>
                  <History className="mr-2 h-4 w-4" />
                  View Activity Log
                </Button>
                {canAddPO && (
                  <Button size="sm" onClick={() => setShowAddPO(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Purchase Order
                  </Button>
                )}
              </div>
            </div>
          </div>

          {purchaseOrderNodes.length > 1 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Purchase Orders
              </div>
              <div className="flex flex-wrap gap-2">
                {purchaseOrderNodes.map((po) => {
                  const s = computePoSummary(po, flatTimelineEvents)
                  const badge = invoicedBadge(s)
                  const active = selectedPo != null && timelineItemId(po) === timelineItemId(selectedPo)
                  return (
                    <button
                      key={timelineItemId(po)}
                      onClick={() => selectPo(timelineItemId(po))}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                        active ? 'border-foreground/30 bg-muted' : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <Badge variant="outline" className="text-[10px]">
                        {po.envelope.idDisplay}
                      </Badge>
                      <span className="text-sm font-semibold">{s.poMoney.toString()}</span>
                      <Badge variant="outline" className={`text-[10px] ${badge.className}`}>
                        {badge.label}
                      </Badge>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
            <div className="space-y-5 min-w-0">
              {(() => {
                if (showAddPoForm)
                  return (
                    <Card>
                      <CardContent className="pt-6">
                        <PurchaseOrderFormWrapper
                          traderId={toInteger(traderId)}
                          orderId={toInteger(orderId)}
                          onSuccess={() => setShowAddPO(false)}
                          onCancel={() => (purchaseOrderNodes.length > 0 ? setShowAddPO(false) : navigate(-1))}
                        />
                      </CardContent>
                    </Card>
                  )
                if (summary && selectedPo)
                  return (
                    <>
                      <Card id={`${timelineItemId(selectedPo)}`}>
                        <CardContent className="@container pt-6">
                          {editingPoId === timelineItemId(selectedPo) ? (
                            <EventFormWrapper
                              eventType={PERMISSION_ENTITY_PURCHASE_ORDER}
                              traderId={toInteger(traderId)}
                              orderId={toInteger(orderId)}
                              editEventId={timelineItemId(selectedPo)}
                              onSuccess={() => setEditingPoId(null)}
                              onCancel={() => setEditingPoId(null)}
                              isAdminContext={false}
                            />
                          ) : (
                            <>
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">{selectedPo.envelope.idDisplay}</Badge>
                                  {domainListItemNumber(selectedPo) ? (
                                    <span>#{domainListItemNumber(selectedPo)}</span>
                                  ) : null}
                                  <Badge variant="secondary">{selectedPo.envelope.statusLabel}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  {(canAddInvoice || canAddOperational) && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button size="sm" variant="outline">
                                          <Plus className="h-4 w-4 mr-2" />
                                          Add Event
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {canAddInvoice && (
                                          <DropdownMenuItem
                                            onClick={() => setAddingChildType(PERMISSION_ENTITY_INVOICE)}
                                          >
                                            Add Invoice
                                          </DropdownMenuItem>
                                        )}
                                        {canAddOperational && (
                                          <DropdownMenuItem
                                            onClick={() => setAddingChildType(PERMISSION_ENTITY_OPERATIONAL)}
                                          >
                                            Add Operational
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `${urlPrefixApp}/${ROUTE_PAYABLE}/order/${orderId}?purchaseOrderId=${timelineItemId(selectedPo)}`
                                      )
                                    }
                                  >
                                    <CircleDollarSign className="mr-2 h-4 w-4" />
                                    Create Payable
                                  </Button>
                                  <EventActions
                                    eventId={timelineItemId(selectedPo)}
                                    eventType={PERMISSION_ENTITY_PURCHASE_ORDER}
                                    orderId={toInteger(orderId)}
                                    traderId={toInteger(traderId)}
                                    timelineActionContext={timelineActionContext}
                                    timelineEventsLoading={isTimelineTreeLoading}
                                    onEdit={() => setEditingPoId(timelineItemId(selectedPo))}
                                    onSuccess={() => setEditingPoId(null)}
                                    isEditing={false}
                                  />
                                </div>
                              </div>
                              <div className="flex flex-wrap items-start gap-x-10 gap-y-6">
                                <div className="inline-flex max-w-full flex-col gap-6">
                                  <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground">Order Value</p>
                                      <p className="text-2xl @xl:text-3xl font-bold tracking-tight whitespace-nowrap tabular-nums">
                                        {summary.poMoney.toString()}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Issue Date</p>
                                      <p className="font-semibold">
                                        {dateFormatted(selectedPo.model.effectiveDate) ?? 'N/A'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Target Delivery</p>
                                      <p className="font-semibold">
                                        {selectedPo.model.endDate ? dateFormatted(selectedPo.model.endDate) : 'N/A'}
                                      </p>
                                    </div>
                                    {selectedPo.model.deliveredDate != null && (
                                      <div>
                                        <p className="text-sm text-muted-foreground">Delivered</p>
                                        <p className="font-semibold">{dateFormatted(selectedPo.model.deliveredDate)}</p>
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="flex flex-wrap justify-between gap-x-4 mb-1">
                                      <p className="text-sm text-muted-foreground">
                                        Invoicing Progress
                                        {summary.invoicedShare
                                          ? ` · ${summary.invoicedShare.toString({ digits: 1 })}`
                                          : ''}
                                      </p>
                                      <p className="text-sm font-semibold">{summary.invoicedMoney.toString()}</p>
                                    </div>
                                    <Progress
                                      value={summary.invoicedShare ? summary.invoicedShare.value : 0}
                                      max={MAX_BASIS_POINTS}
                                      className="h-2"
                                      indicatorClassName={
                                        summary.invoicedMoney.greaterThanOrEqualTo(summary.poMoney)
                                          ? 'bg-green-500'
                                          : undefined
                                      }
                                    />
                                    {summary.otherCurrencyInvoiced.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-border space-y-1">
                                        {summary.otherCurrencyInvoiced.map(([currency, money]) => (
                                          <p key={currency} className="text-xs text-muted-foreground">
                                            {money.toString()} invoiced (other currency — not included in progress)
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {(selectedPoDocuments.length > 0 || order.updatedAt) && (
                                  <div className="min-w-60 flex-1">
                                    {selectedPoDocuments.length > 0 && (
                                      <p className="text-sm text-muted-foreground mb-1">Documents</p>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                      {selectedPoFilePreviewContext && selectedPoDocuments.length > 0 && (
                                        <FileBadgeList
                                          documents={selectedPoDocuments}
                                          previewContext={selectedPoFilePreviewContext}
                                          getEventUrl={getEventUrl}
                                          className="flex-row flex-wrap items-start justify-start gap-2"
                                        />
                                      )}
                                    </div>
                                    {order.updatedAt && (
                                      <p className="mt-3 text-xs text-muted-foreground">
                                        Updated {parseTimestamp(order.updatedAt)?.toKlearDate().toString()}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      {addingChildType && (
                        <Card>
                          <CardContent className="pt-6">
                            <EventFormWrapper
                              eventType={addingChildType}
                              traderId={toInteger(traderId)}
                              orderId={toInteger(orderId)}
                              parentEventId={timelineItemId(selectedPo)}
                              onSuccess={() => setAddingChildType(null)}
                              onCancel={() => setAddingChildType(null)}
                              isAdminContext={false}
                            />
                          </CardContent>
                        </Card>
                      )}

                      {sortedChildren.length > 0 && (
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Invoices & Operations
                        </div>
                      )}
                      {isTimelineTreeLoading && (
                        <p className="text-sm text-muted-foreground text-center py-8">Loading timeline…</p>
                      )}
                      {sortedChildren.map((child) => (
                        <Card key={timelineItemId(child)}>
                          <CardContent className="pt-6">{renderEventItem(child)}</CardContent>
                        </Card>
                      ))}
                    </>
                  )
                return (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      No purchase orders yet
                    </CardContent>
                  </Card>
                )
              })()}
            </div>

            <div className="lg:sticky lg:top-36 lg:self-start">
              <Card>
                <CardContent className="pt-5">
                  <div className="font-bold mb-1">Order-to-cash Timeline</div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className="text-xs text-muted-foreground">Select a step to jump to it</p>
                    <Select value={sortOrder} onValueChange={(v: 'newest' | 'oldest') => setSortOrder(v)}>
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oldest">Oldest</SelectItem>
                        <SelectItem value="newest">Newest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(() => {
                    if (isTimelineTreeLoading)
                      return <p className="text-sm text-muted-foreground text-center py-8">Loading timeline…</p>
                    if (timelineSteps.length === 0)
                      return <p className="text-sm text-muted-foreground text-center py-8">No timeline events yet</p>
                    return (
                      <div className="lg:max-h-[calc(100vh-21.5rem)] lg:overflow-y-auto lg:pr-1 lg:-mr-1">
                        {timelineSteps.map((timelineStep, i) => {
                          const last = i === timelineSteps.length - 1
                          if (timelineStep.kind === 'delivery') {
                            return (
                              <TimelineStepButton
                                key={`delivery-${timelineStep.label}`}
                                active={false}
                                last={last}
                                icon={<Truck className="h-4 w-4" />}
                                iconClassName="bg-muted text-muted-foreground"
                                title={timelineStep.label}
                                date={dateFormattedOrDash(timelineStep.date)}
                                onClick={() => {
                                  if (selectedPoNodeId != null) revealStep(selectedPoNodeId)
                                }}
                              />
                            )
                          }
                          const event = timelineStep.event
                          const eventId = timelineItemId(event)
                          const si = stepIcon(event.type)
                          const eventNumber = domainListItemNumber(event)
                          return (
                            <TimelineStepButton
                              key={eventId}
                              active={eventId === focusEventId}
                              last={last}
                              icon={si.icon}
                              iconClassName={si.className}
                              idDisplay={event.envelope.idDisplay}
                              title={`${orderEventTypeLabel(event.type)}${eventNumber ? ` #${eventNumber}` : ''}`}
                              date={dateFormattedOrDash(domainListItemEffectiveDate(event))}
                              status={
                                orderEventShowsStatus(event.type) && showsStatusToTrader(event)
                                  ? event.envelope.statusLabel
                                  : undefined
                              }
                              onClick={() => revealStep(eventId)}
                            />
                          )
                        })}
                      </div>
                    )
                  })()}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Activity Log</DialogTitle>
            </DialogHeader>
            <EventStatus
              orderId={toInteger(orderId)}
              sortOrder={sortOrder}
              onSelectEvent={(id) => {
                selectEventById(id)
                setActivityOpen(false)
              }}
            />
          </DialogContent>
        </Dialog>

        {advanceRecoveryTarget != null && traderIdNum > 0 && (
          <AdvanceRecoveryModal
            traderId={traderIdNum}
            offerId={advanceRecoveryTarget.id}
            offerIdDisplay={advanceRecoveryTarget.idDisplay}
            open={advanceRecoveryTarget != null}
            onOpenChange={(open) => {
              if (!open) setAdvanceRecoveryTarget(null)
            }}
            onSuccess={() => {
              void utils.orderEvent.timelineTree.invalidate({ orderId: toNumber(orderId) })
              void utils.invoiceOffer.list.invalidate()
            }}
          />
        )}
      </div>
    </OrderEventDocumentsProvider>
  )
}
